import { useEffect, useRef, useState } from "react";

import useAxios from "@hooks/use-axios";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 20;

const useAuditLog = () => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true });
    const [entries, setEntries] = useState([]);
    const [counts, setCounts] = useState({ all: 0, ops: 0, brand: 0, publisher: 0 });
    const [scope, setScope] = useState("all");
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    const isFirstRender = useRef(true);

    const fetchLog = async (currentScope, currentSearch, page = 1, limit = DEFAULT_LIMIT) => {
        // The backend's query param is `actor_type` (values: ops/brand/publisher) with no
        // concept of "scope" — sending `scope` was silently ignored, so every tab returned
        // the same unfiltered set. "all" has no backend equivalent, so omit the param for it.
        const res = await axiosInstance.get("v1/audit-logs", {
            params: { actor_type: currentScope === "all" ? undefined : currentScope, search: currentSearch || undefined, page, limit }
        });
        const result = res.data || res;
        const items = result.data?.items || result.items || [];
        setEntries(items);
        setCounts(result.data?.counts || result.counts || { all: 0, ops: 0, brand: 0, publisher: 0 });
        setPagination({
            total: result.total ?? items.length,
            page : result.page ?? page,
            limit: result.limit ?? limit
        });
    };

    useEffect(() => {
        fetchLog(scope, search, 1, pagination.limit);
    }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => fetchLog(scope, search, 1, pagination.limit), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchLog(scope, search, page, limit);
    const handlePerRowsChange = (limit) => fetchLog(scope, search, 1, limit);

    return { entries, counts, pagination, loading, scope, setScope, search, setSearch, handlePageChange, handlePerRowsChange };
};

export default useAuditLog;
